/**
 * API request history management
 * Tracks last 3 sent API requests with their actual payload (including uploaded URLs)
 */

const STORAGE_KEY = 'apiRequestHistory';
const MAX_HISTORY_ITEMS = 3;

export interface CategorizedUrls {
  image_urls: string[];
  video_urls: string[];
  audio_urls: string[];
}

export interface SentRequest {
  timestamp: number;
  workflowId: number;
  workflowTitle: string;
  inputs: Record<string, any>;
  categorizedUrls?: CategorizedUrls;
}

/**
 * Get API request history from localStorage
 * Returns array of sent requests ordered by most recent first (max 3)
 */
export function getSentRequestHistory(): SentRequest[] {
  if (typeof window === 'undefined') return [];

  try {
    const historyJson = localStorage.getItem(STORAGE_KEY);
    const history = historyJson ? JSON.parse(historyJson) : [];
    return Array.isArray(history) ? history : [];
  } catch (error) {
    console.warn('Failed to load request history from localStorage:', error);
    return [];
  }
}

/**
 * Save a sent request to history (most recent first, max 3 items)
 * Call this after successful API response with sentPayload
 */
export function saveSentRequest(request: SentRequest): void {
  if (typeof window === 'undefined' || !request) return;

  try {
    const currentHistory = getSentRequestHistory();

    // Add to front, limit to MAX_HISTORY_ITEMS
    const newHistory = [request, ...currentHistory].slice(0, MAX_HISTORY_ITEMS);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
  } catch (error) {
    console.warn('Failed to save request to history:', error);
  }
}

/**
 * Clear all API request history
 */
export function clearSentRequestHistory(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear request history:', error);
  }
}

/**
 * Format timestamp to readable time string
 */
export function formatRequestTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

/**
 * Format timestamp to readable date + time string (for histories spanning days)
 */
export function formatRequestDateTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}
